/**
 * Terms of Service Page
 * Legal terms governing use of CalWMP service
 */

import { Link } from 'react-router-dom';

export function Terms() {
  const lastUpdated = '2025-09-30';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Terms of Service</h1>

      <p className="text-sm text-neutral-600 mb-8">
        <strong>Last Updated:</strong> {lastUpdated}
      </p>

      <div className="prose prose-neutral max-w-none">
        {/* Introduction */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Acceptance of Terms</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Welcome to CalWMP. These Terms of Service (&quot;Terms&quot;) constitute a legal agreement between you (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;)
            and CalWMP (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) governing your use of the CalWMP waste management plan creation service
            available at calwmp.com (the &quot;Service&quot;).
          </p>
          <p className="text-neutral-700 leading-relaxed mb-4">
            By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by
            these Terms and our <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</Link>.
            If you do not agree to these Terms, you may not use the Service.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            <strong>Important:</strong> These Terms contain provisions that limit our liability and require arbitration
            of disputes. Please read them carefully.
          </p>
        </section>

        {/* Description of Service */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Description of Service</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP is a <strong>free</strong> online tool designed to help users create waste management plans for
            California CalGreen Building Code Section 5.408 compliance. The Service provides:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Plan creation and editing tools</li>
            <li>PDF document generation</li>
            <li>Guest mode with browser-based storage and email-based shareable links</li>
            <li>Optional authenticated accounts for plan management</li>
            <li>Compliance guidance and reference materials</li>
          </ul>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 my-4">
            <p className="text-amber-900 mb-2">
              <strong>Important Limitations:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-amber-900">
              <li>CalWMP is <strong>NOT</strong> affiliated with any government agency, regulatory body, or enforcement authority</li>
              <li>The Service is provided <strong>FREE OF CHARGE</strong> on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis</li>
              <li>We make <strong>NO WARRANTIES</strong> regarding accuracy, completeness, or compliance with any regulations</li>
              <li>Plans generated by CalWMP <strong>MUST</strong> be reviewed by qualified professionals before submission to any authority</li>
            </ul>
          </div>
        </section>

        {/* User Accounts */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">User Accounts</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Guest Mode</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You may use the Service without creating an account. In guest mode:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Your plan data is stored locally in your browser&apos;s storage</li>
            <li>We cannot access or recover data stored in your browser</li>
            <li>You may request a shareable link by providing an email address</li>
            <li>Shareable links expire after 30 days</li>
            <li>Clearing your browser data will permanently delete your local plans</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Authenticated Accounts</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You may create an account to save and manage multiple plans. When creating an account:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>You must provide a valid email address</li>
            <li>You are responsible for maintaining the confidentiality of your credentials</li>
            <li>You are responsible for all activities under your account</li>
            <li>You must notify us immediately of any unauthorized access</li>
            <li>You may not share your account or transfer it to another person</li>
            <li>You may not create multiple accounts to circumvent Service limitations</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Account Eligibility</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            By creating an account, you represent and warrant that:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>You are at least 18 years of age or the age of majority in your jurisdiction</li>
            <li>You have the legal capacity to enter into binding contracts</li>
            <li>All information you provide is accurate and current</li>
            <li>Your use of the Service complies with all applicable laws</li>
          </ul>
        </section>

        {/* Acceptable Use Policy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Acceptable Use Policy</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Prohibited Activities</h3>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Violate any applicable federal, state, local, or international law or regulation</li>
            <li>Use the Service to create fraudulent, misleading, or deceptive plans</li>
            <li>Impersonate any person or entity or falsely state your affiliation with any person or entity</li>
            <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
            <li>Attempt to gain unauthorized access to any portion of the Service, user accounts, or systems</li>
            <li>Use any automated means (bots, scrapers, crawlers) to access or collect data from the Service</li>
            <li>Transmit viruses, malware, or any malicious code</li>
            <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
            <li>Remove, modify, or obscure any copyright, trademark, or proprietary notices</li>
            <li>Use the Service in any manner that could damage, disable, or overburden our infrastructure</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Enforcement</h3>
          <p className="text-neutral-700 leading-relaxed">
            We reserve the right to investigate and take appropriate action against anyone who, in our sole discretion,
            violates these Terms, including without limitation removing content, suspending or terminating accounts, and
            reporting violations to law enforcement authorities.
          </p>
        </section>

        {/* User Content */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">User Content and Ownership</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Your Content</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You retain all ownership rights to the waste management plans, project information, and other content you
            create using the Service (&quot;User Content&quot;). CalWMP does not claim any ownership rights over your User Content.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">License to CalWMP</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            By using the Service, you grant CalWMP a limited, non-exclusive, worldwide, royalty-free license to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Store, process, and transmit your User Content as necessary to provide the Service</li>
            <li>Create backups and copies for reliability and disaster recovery purposes</li>
            <li>Use aggregated, anonymized data derived from User Content for analytics and Service improvement</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            This license terminates when you delete your User Content or account, except for content retained in backups
            or as required by law.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Your Responsibilities</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">You are solely responsible for:</p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>The accuracy, completeness, and legality of your User Content</li>
            <li>Ensuring your User Content does not violate any third-party rights</li>
            <li>Maintaining backup copies of your User Content</li>
            <li>Verifying that generated plans meet your compliance requirements</li>
          </ul>
        </section>

        {/* Intellectual Property */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Intellectual Property</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">CalWMP&apos;s Rights</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            The Service, including all software, content, trademarks, service marks, logos, and design elements, is owned
            by CalWMP or its licensors and is protected by United States and international copyright, trademark, patent,
            trade secret, and other intellectual property laws.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Limited License to Use</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service
            for its intended purpose in accordance with these Terms. This license does not include any right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Resell, lease, or sublicense access to the Service</li>
            <li>Use the Service to create competing products or services</li>
            <li>Copy, modify, or create derivative works of the Service</li>
            <li>Use CalWMP trademarks, logos, or branding without written permission</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Feedback</h3>
          <p className="text-neutral-700 leading-relaxed">
            If you provide feedback, suggestions, or ideas about the Service (&quot;Feedback&quot;), you grant us a perpetual,
            irrevocable, worldwide, royalty-free license to use, modify, and incorporate such Feedback into our products
            and services without any obligation to you.
          </p>
        </section>

        {/* Disclaimers */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Disclaimers and &quot;AS IS&quot; Provision</h2>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 my-6">
            <p className="text-red-900 font-semibold mb-4 text-lg">
              CRITICAL: READ CAREFULLY
            </p>
            <p className="text-red-900 mb-4">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              NON-INFRINGEMENT, ACCURACY, COMPLETENESS, OR RELIABILITY.
            </p>
            <p className="text-red-900 mb-4">
              CALWMP MAKES NO WARRANTY THAT:
            </p>
            <ul className="list-disc list-inside space-y-2 text-red-900 mb-4">
              <li>The Service will meet your requirements or expectations</li>
              <li>The Service will be uninterrupted, timely, secure, or error-free</li>
              <li>Generated plans will comply with any building codes, regulations, or requirements</li>
              <li>Generated plans will be accepted by any authority having jurisdiction</li>
              <li>Any errors or defects in the Service will be corrected</li>
              <li>The Service is free from viruses or other harmful components</li>
            </ul>
            <p className="text-red-900">
              YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. WE DO NOT GUARANTEE ANY RESULTS FROM USING THE SERVICE.
            </p>
          </div>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">No Government Affiliation</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP is <strong>NOT</strong> affiliated with, endorsed by, or connected to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>California Department of Resources Recycling and Recovery (CalRecycle)</li>
            <li>California Building Standards Commission (CBSC)</li>
            <li>California Energy Commission (CEC)</li>
            <li>Any local building department or authority having jurisdiction</li>
            <li>Any federal, state, or local government agency</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Third-Party Content</h3>
          <p className="text-neutral-700 leading-relaxed">
            The Service may contain links to third-party websites or resources. We are not responsible for the
            availability, accuracy, or content of such external sites. Links do not imply endorsement.
          </p>
        </section>

        {/* Professional Advice Disclaimer */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Professional Advice Disclaimer</h2>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 my-6">
            <p className="text-red-900 font-semibold mb-4 text-lg">
              NOT LEGAL, REGULATORY, OR PROFESSIONAL ADVICE
            </p>
            <p className="text-red-900 mb-4">
              CALWMP DOES NOT PROVIDE LEGAL, REGULATORY, ENGINEERING, ARCHITECTURAL, OR PROFESSIONAL ADVICE OF ANY KIND.
              THE SERVICE IS AN INFORMATIONAL TOOL ONLY.
            </p>
            <p className="text-red-900 mb-4">
              <strong>YOU MUST:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-red-900 mb-4">
              <li>Have all generated plans reviewed by qualified professionals (architects, engineers, attorneys, etc.)</li>
              <li>Verify compliance with all applicable federal, state, and local laws and regulations</li>
              <li>Consult with the authority having jurisdiction before relying on any plan</li>
              <li>Seek independent professional advice regarding your specific situation</li>
              <li>Understand that building codes and regulations change frequently and vary by jurisdiction</li>
            </ul>
            <p className="text-red-900">
              SUBMITTING A PLAN GENERATED BY CALWMP WITHOUT PROFESSIONAL REVIEW IS DONE AT YOUR OWN RISK. CALWMP ASSUMES
              NO RESPONSIBILITY FOR PLANS THAT ARE REJECTED, INCOMPLETE, OR NON-COMPLIANT.
            </p>
          </div>
        </section>

        {/* Limitation of Liability */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Limitation of Liability</h2>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 my-6">
            <p className="text-red-900 font-semibold mb-4 text-lg">
              LIMITATION OF LIABILITY
            </p>
            <p className="text-red-900 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL CALWMP, ITS OFFICERS, DIRECTORS,
              EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION:
            </p>
            <ul className="list-disc list-inside space-y-2 text-red-900 mb-4">
              <li>Loss of profits, revenue, data, or use</li>
              <li>Business interruption or lost savings</li>
              <li>Costs of procurement of substitute services</li>
              <li>Project delays, rejections, or failures</li>
              <li>Fines, penalties, or regulatory actions</li>
              <li>Damage to professional reputation</li>
              <li>Any other commercial damages or losses</li>
            </ul>
            <p className="text-red-900 mb-4">
              WHETHER ARISING OUT OF BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR OTHERWISE,
              EVEN IF CALWMP HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="text-red-900 mb-4">
              <strong>TO THE EXTENT NOT PROHIBITED BY LAW, OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
              RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-red-900 mb-4">
              <li>THE AMOUNT YOU PAID TO US FOR THE SERVICE (WHICH IS $0.00 AS THE SERVICE IS FREE), OR</li>
              <li>ONE HUNDRED DOLLARS ($100.00)</li>
            </ul>
            <p className="text-red-900">
              SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO
              THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.
            </p>
          </div>
        </section>

        {/* Indemnification */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Indemnification</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You agree to indemnify, defend, and hold harmless CalWMP, its officers, directors, employees, agents,
            affiliates, and licensors from and against any and all claims, damages, obligations, losses, liabilities,
            costs, and expenses (including reasonable attorneys&apos; fees) arising from:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Your use or misuse of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights, including intellectual property or privacy rights</li>
            <li>Your User Content or any plans generated using the Service</li>
            <li>Submission of any plan to any authority or use in any construction project</li>
            <li>Any regulatory action, fine, penalty, or rejection related to plans generated by the Service</li>
            <li>Any claim that your User Content caused damage to a third party</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            This indemnification obligation survives termination of these Terms and your use of the Service.
          </p>
        </section>

        {/* Third-Party Services */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Third-Party Services</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            The Service relies on third-party infrastructure and services, including:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li><strong>Cloudflare:</strong> Hosting, database, CDN, and security services</li>
            <li><strong>Email Services:</strong> For sending shareable links and notifications</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Your use of the Service may be subject to the terms and conditions of these third-party providers. We are
            not responsible for any third-party service failures, interruptions, or changes that affect the Service.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            Reference to the California Building Code, CalGreen, or any regulations does not imply endorsement or
            affiliation with any regulatory body.
          </p>
        </section>

        {/* Modifications to Terms */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Modifications to Terms</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We reserve the right to modify these Terms at any time in our sole discretion. When we make changes, we will:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Update the &quot;Last Updated&quot; date at the top of this page</li>
            <li>Post the revised Terms on the Service</li>
            <li>For material changes, provide notice through the Service or via email to registered users</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
            If you do not agree to the modified Terms, you must stop using the Service.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            It is your responsibility to review these Terms periodically for changes.
          </p>
        </section>

        {/* Modifications to Service */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Modifications to Service</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We reserve the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Modify, suspend, or discontinue the Service (or any part thereof) at any time</li>
            <li>Change features, functionality, or availability without notice</li>
            <li>Impose limits on certain features or restrict access to parts of the Service</li>
            <li>Remove or modify any content or features</li>
          </ul>
          <p className="text-neutral-700 leading-relaxed">
            We are not liable to you or any third party for any modification, suspension, or discontinuation of the Service.
          </p>
        </section>

        {/* Termination */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Termination</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Termination by You</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You may stop using the Service at any time. For authenticated accounts, you may request account deletion
            by contacting <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>.
            Upon deletion:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Your account and User Content will be deleted from our active systems</li>
            <li>You will no longer have access to your saved plans</li>
            <li>Backups may be retained for a reasonable period for disaster recovery</li>
            <li>Anonymized, aggregated data may be retained for analytics</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Termination by CalWMP</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any
            reason, including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Breach of these Terms</li>
            <li>Violation of applicable laws or regulations</li>
            <li>Fraudulent, abusive, or otherwise objectionable behavior</li>
            <li>Prolonged inactivity</li>
            <li>Request by law enforcement or government agencies</li>
            <li>Discontinuation of the Service</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Effect of Termination</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Upon termination:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Your right to use the Service immediately ceases</li>
            <li>All licenses granted to you terminate</li>
            <li>Sections of these Terms that by their nature should survive termination will survive, including but not
                limited to: ownership provisions, warranty disclaimers, indemnification, and limitations of liability</li>
          </ul>
        </section>

        {/* Dispute Resolution */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Dispute Resolution and Arbitration</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Informal Resolution</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Before filing a claim, you agree to contact us at <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a> and
            attempt to resolve the dispute informally. We will attempt to resolve disputes in good faith within 30 days.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Arbitration Agreement</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If informal resolution fails, you agree that any dispute, claim, or controversy arising out of or relating to
            these Terms or the Service shall be resolved by binding arbitration in accordance with the rules of the
            American Arbitration Association, rather than in court, except that:
          </p>
          <ul className="list-disc list-inside space-y-2 text-neutral-700 mb-4">
            <li>Either party may seek injunctive or equitable relief in court for intellectual property or confidentiality matters</li>
            <li>You may assert claims in small claims court if they qualify</li>
          </ul>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Class Action Waiver</h3>
          <p className="text-neutral-700 leading-relaxed">
            You agree to bring claims only in your individual capacity and not as a plaintiff or class member in any
            purported class or representative proceeding. Arbitration will be on an individual basis; class arbitrations
            and class actions are not permitted.
          </p>
        </section>

        {/* Governing Law */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Governing Law and Jurisdiction</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            These Terms and your use of the Service are governed by the laws of the State of California, United States,
            without regard to its conflict of law provisions.
          </p>
          <p className="text-neutral-700 leading-relaxed mb-4">
            To the extent arbitration does not apply, you agree to submit to the personal and exclusive jurisdiction of
            the state and federal courts located in California for resolution of any disputes.
          </p>
          <p className="text-neutral-700 leading-relaxed">
            The United Nations Convention on Contracts for the International Sale of Goods does not apply to these Terms.
          </p>
        </section>

        {/* Miscellaneous */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Miscellaneous Provisions</h2>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3 mt-6">Entire Agreement</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            These Terms, together with our <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</Link> and
            any other legal notices published by us on the Service, constitute the entire agreement between you and CalWMP
            concerning the Service and supersede all prior agreements and understandings.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Severability</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining provisions
            shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary
            to make it valid and enforceable.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Waiver</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other
            term. Our failure to assert any right or provision under these Terms does not constitute a waiver of such right
            or provision.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Assignment</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may
            assign these Terms without restriction. Any attempted assignment in violation of this section is void.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Force Majeure</h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            CalWMP shall not be liable for any failure or delay in performance due to causes beyond our reasonable control,
            including but not limited to acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or
            military authorities, fire, floods, accidents, network infrastructure failures, strikes, or shortages of
            transportation facilities, fuel, energy, labor, or materials.
          </p>

          <h3 className="text-xl font-semibold text-neutral-900 mb-3">Survival</h3>
          <p className="text-neutral-700 leading-relaxed">
            All provisions of these Terms which by their nature should survive termination shall survive, including without
            limitation: ownership provisions, warranty disclaimers, indemnification, limitations of liability, and dispute
            resolution provisions.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Contact Information</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            If you have questions, concerns, or disputes regarding these Terms, please contact us:
          </p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6">
            <p className="text-neutral-900 mb-2">
              <strong>Email:</strong> <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 underline">support@calwmp.com</a>
            </p>
            <p className="text-neutral-900 mb-2">
              <strong>Subject Line:</strong> Include &quot;Terms of Service&quot; for faster routing
            </p>
            <p className="text-neutral-900">
              <strong>Response Time:</strong> We aim to respond to all inquiries within 5 business days
            </p>
          </div>
        </section>

        {/* Related Policies */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Related Legal Documents</h2>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Please also review these important documents:
          </p>
          <ul className="space-y-2 text-neutral-700">
            <li>
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>
              {' '}- How we collect, use, and protect your information
            </li>
            <li>
              <Link to="/disclaimer" className="text-primary-600 hover:text-primary-700 underline">
                Disclaimer
              </Link>
              {' '}- Important limitations and warnings about using CalWMP
            </li>
            <li>
              <Link to="/accessibility" className="text-primary-600 hover:text-primary-700 underline">
                Accessibility Statement
              </Link>
              {' '}- Our commitment to accessibility and how to report issues
            </li>
          </ul>
        </section>

        {/* Acknowledgment */}
        <section className="mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-900 mb-4">
              <strong>Acknowledgment:</strong>
            </p>
            <p className="text-blue-900">
              BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE
              TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU ARE NOT AUTHORIZED TO USE THE SERVICE.
            </p>
          </div>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          These Terms of Service were last updated on {lastUpdated}. Previous versions are available upon request.
        </p>
      </div>
    </div>
  );
}